import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Tag, ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * CategoryChips — filtro compacto horizontal con scroll fluido (Iter 53).
 *
 * Fila 1: categorías principales (scroll horizontal + botones ‹ ›).
 * Fila 2 (solo si hay categoría activa): subcategorías con separador visual
 * (fondo distinto, indentado, botón ✕ para deseleccionar la categoría rápido).
 */
export default function CategoryChips({
  categoryId = null,
  subcategoryId = null,
  onChange,
  testidPrefix = "cat-chips",
}) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/subcategories")]).then(([c, s]) => {
      setCategories(c.data.filter((x) => x.active !== false));
      setSubcategories(s.data.filter((x) => x.active !== false));
    }).catch(() => {});
  }, []);

  const visibleSubs = useMemo(() => {
    if (!categoryId) return [];
    return subcategories.filter((s) => s.category_id === categoryId);
  }, [categoryId, subcategories]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) || null,
    [categoryId, categories],
  );

  const pickCategory = (cid) => onChange?.({ category_id: cid, subcategory_id: null });
  const pickSub = (sid) => onChange?.({ category_id: categoryId, subcategory_id: sid });

  return (
    <div className="space-y-1" data-testid={`${testidPrefix}-container`}>
      <ScrollableRow testid={`${testidPrefix}-row`}>
        <div className="flex items-center gap-0.5 pl-1 pr-0.5 shrink-0 text-muted-foreground">
          <Tag className="w-3.5 h-3.5" />
        </div>
        <Chip
          active={!categoryId}
          onClick={() => pickCategory(null)}
          testid={`${testidPrefix}-all`}
        >
          Todas
        </Chip>
        {categories.map((c) => (
          <Chip
            key={c.id}
            active={categoryId === c.id}
            onClick={() => pickCategory(c.id)}
            testid={`${testidPrefix}-cat-${c.id}`}
          >
            {c.name}
          </Chip>
        ))}
      </ScrollableRow>

      {activeCategory && (
        <div
          className="rounded-md bg-primary/5 border-l-4 border-primary/50 pl-2 py-1"
          data-testid={`${testidPrefix}-subs-container`}
        >
          <ScrollableRow testid={`${testidPrefix}-subs`}>
            <span className="text-[10px] font-bold uppercase text-primary/80 tracking-wider shrink-0 pl-0.5 pr-2 flex items-center gap-1">
              {activeCategory.name}
              <button
                onClick={() => pickCategory(null)}
                className="hover:bg-primary/10 rounded-full p-0.5"
                title="Quitar filtro"
                data-testid={`${testidPrefix}-clear-cat`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
              <span className="opacity-40">›</span>
            </span>
            <Chip
              small
              active={!subcategoryId}
              onClick={() => pickSub(null)}
              testid={`${testidPrefix}-sub-all`}
            >
              Todas
            </Chip>
            {visibleSubs.map((s) => (
              <Chip
                small
                key={s.id}
                active={subcategoryId === s.id}
                onClick={() => pickSub(s.id)}
                testid={`${testidPrefix}-sub-${s.id}`}
              >
                {s.name}
              </Chip>
            ))}
            {visibleSubs.length === 0 && (
              <span className="text-[11px] text-muted-foreground italic pl-2">Sin subcategorías</span>
            )}
          </ScrollableRow>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testid, small = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 whitespace-nowrap font-medium rounded-full border transition-colors ${
        small ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-1"
      } ${
        active
          ? "bg-primary text-primary-foreground border-primary shadow-sm"
          : "bg-background text-foreground/80 border-border hover:bg-accent hover:border-primary/40"
      }`}
    >
      {children}
    </button>
  );
}

/** Row scrollable con botones ‹ › que solo aparecen cuando hay overflow. */
function ScrollableRow({ children, testid }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    el.addEventListener("scroll", update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); mo.disconnect(); };
  }, [update]);

  const scrollBy = (delta) => ref.current?.scrollBy({ left: delta, behavior: "smooth" });

  return (
    <div className="relative">
      {canLeft && (
        <button
          onClick={() => scrollBy(-240)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/95 backdrop-blur border rounded-full w-6 h-6 flex items-center justify-center shadow-sm hover:bg-accent"
          data-testid={`${testid}-scroll-left`}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      )}
      <div
        ref={ref}
        className="flex items-center gap-1.5 overflow-x-auto py-0.5 px-6"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        data-testid={testid}
      >
        {children}
      </div>
      {canRight && (
        <button
          onClick={() => scrollBy(240)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/95 backdrop-blur border rounded-full w-6 h-6 flex items-center justify-center shadow-sm hover:bg-accent"
          data-testid={`${testid}-scroll-right`}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
